














































































































import { Component, Vue, Watch, Prop } from 'vue-property-decorator';

/**
 * Helpers
 */
import { getFullName } from "@/utils/users-helpers";
import { getDateString, sortByDate, smartDisplayMinutes, addMinutes, getLocaleDateString, getShortTimeString, getElegantDateString } from "@/utils/date-helpers";

/**
 * Components
 */
import BookingCreationDialog from './BookingCreationDialog.vue';
import BookingDetailsDialog from './BookingDetailsDialog.vue';
import Calendar from './Calendar.vue';

/**
 * Types
 */
import { CalendarTimedEvent } from './Calendar.vue';
import { FilterModel } from "@/components/_global/FilterControl.vue";
import { ResourceBookableTimeRange, ResourceBooking, BookingState } from '@/api/bookings';

/**
 * Composant de réservation horaires.
 */
@Component({
	components: {
		'app-calendar': Calendar,
		'booking-creation-dialog': BookingCreationDialog,
		'booking-details-dialog': BookingDetailsDialog
	}
})
export default class BookingCalendar extends Vue {
	$refs!: {
		bookingCreationDialog: BookingCreationDialog,
		bookingDetailsDialog: BookingDetailsDialog
	};

	/** Injections */
	getFullName = getFullName;

	/** Constantes */
	SYMBOL_LEGEND = '&#9632;';
	COLOR_BOOKING = '#1867c0';
	COLOR_FREE = '#18c067';
	COLOR_DISABLED = '#777777';
	COLOR_PENDING = '#FF9800';

	/** Propriétés */
	@Prop()
	fetchDayBookings!: (day: number, month: number, year: number) => ResourceBooking[];
	@Prop()
	fetchDayBookableTimeRanges!: (day: number, month: number, year: number) => ResourceBookableTimeRange[];

	@Prop({ default: 'fr-FR' })
	locale!: string;

	@Prop()
	onMoreClick!: (booking: any) => void;

	@Prop()
	users!: any[];

	@Prop()
	services!: any[];

	@Prop({ default: false })
	isLoading!: boolean;
	@Prop({ default: false })
	isActionLoading!: boolean;

	@Prop({ default: 10000 })
	automaticRefreshPeriodInMS!: number;


	/** Attributs */
	// Légende
	legend = [{
		label: 'booking.booking_waiting',
		symbol: this.SYMBOL_LEGEND,
		color: this.COLOR_PENDING,
		condition: (event: CalendarTimedEvent) => event.isWaitingForSynchronization,
	}, {
		label: 'booking.booking_confirmed',
		symbol: this.SYMBOL_LEGEND,
		color: this.COLOR_BOOKING,
		cursor: 'pointer',
		condition: (event: CalendarTimedEvent) => !event.isWaitingForSynchronization && event.isReadable,
	}, {
		label: 'booking_resources.not_bookable',
		symbol: this.SYMBOL_LEGEND,
		color: this.COLOR_DISABLED,
		cursor: 'not-allowed',
		condition: (event: CalendarTimedEvent) => (event.isReserved || event.isDisabled) && !event.isReadable,
	}, {
		label: 'booking_resources.bookable',
		symbol: this.SYMBOL_LEGEND,
		color: this.COLOR_FREE,
		condition: (event: CalendarTimedEvent) => !event.isReserved,
	}];

	// Le menu du datepicker
	menu = false;

	// Les filtres
	filterModel: FilterModel | null = null;
	serviceFilterModel: FilterModel | null = null;

	// La date courante sélectionnée avec le datepicker
	date: string = getDateString(new Date());
	get dateFormatted() {
		return getElegantDateString(new Date(Date.parse(this.date)), this.locale);
	}

	polling: any = null;

	// Loading lorsque la date change
	isDateChangedLoading = false;

	// L'heure minimale et l'heure maximale des calendriers
	get minHour() {
		return Math.min(...this.events.map(e => parseInt(e.time.split(':')[0], 10)));
	}
	get maxHour() {
		return Math.max(...this.events.map(e => parseInt(addMinutes(e.time, e.duration - 1).split(':')[0], 10) + 1));
	}

	// Le zoom des calendriers
	minZoom = 1;
	get maxZoom() {
		if (this.events.length === 0) {
			return 1;
		}
		const minDuration = Math.min(...this.events.map((e) => e.duration));
		return Math.max(1, Math.round(2.5 * (this.maxHour - this.minHour) / minDuration));
	}
	zoom: number = 1;

	// Les réservations du jour
	dayBookings: any[] = [];
	// Les créneaux réservables du jour
	dayBookableTimeRanges: ResourceBookableTimeRange[] = [];

	// Les évènements des calendriers
	get events(): CalendarTimedEvent[] {
		const eventsTimeRanges = this.dayBookableTimeRanges.map(
			(tr) => {
				const durationInMinutes = tr.durationInMinutes;
				const time = getShortTimeString(tr.startDate);

				return {
					title: `${addMinutes(time, 0)} - ${addMinutes(time, durationInMinutes)} (${smartDisplayMinutes(durationInMinutes)}) `,
					date: getDateString(tr.startDate),
					time: time,
					duration: durationInMinutes,
					isDisabled: !tr.isBookable,
					isReserved: false,
					isEditable: false,
					isReadable: false,
					isWaitingForSynchronization: false,
					color: '',
					cursor: 'default',
					customData: {
						service: tr.service,
						bookabilityMinimumDurationInMinutes: tr.bookabilityMinimumDurationInMinutes,
						bookabilityMaximumDurationInMinutes: tr.bookabilityMaximumDurationInMinutes,
						bookabilityStepInMinutes: tr.bookabilityStepInMinutes
					}
				};
			});

		const eventsBookings = this.dayBookings.map(
			(db) => {
				const durationInMinutes = db.durationInMinutes;
				const time = getShortTimeString(db.startDate);

				return {
					title: `${addMinutes(time, 0)} - ${addMinutes(time, durationInMinutes)} (${smartDisplayMinutes(durationInMinutes)}) `,
					date: getDateString(db.startDate),
					time: time,
					duration: durationInMinutes,
					isDisabled: false,
					isReserved: true,
					color: '',
					cursor: 'default',
					isWaitingForSynchronization: db.state === BookingState.WAITING_FOR_CONFIRMATION || db.state === BookingState.WAITING_FOR_CANCELLATION,
					isReadable: db.isReadable,
					isEditable: db.isEditable,
					customData: {
						service: db.service,
						user: db.user
					}
				};
			});
		return [
			...eventsTimeRanges,
			...eventsBookings
		].map((e: CalendarTimedEvent) => {
			const correspondingLegend = this.legend.find(l => l.condition(e));
			return {
				...e,
				color: correspondingLegend ? correspondingLegend.color : 'black',
				cursor: correspondingLegend ? correspondingLegend.cursor : 'default'
			};
		});
	}

	get zoomLabels() {
		return [...Array(this.maxZoom - this.minZoom + 1).keys()].map(h => this.sliderLabelWithSuffix(h + this.minZoom));
	}

	get filtersDefinitions() {
		const fd: any = {};

		if (this.users != null && this.users.length > 1) {
			fd.USER = {
				label: this.$t('booking.user'),
				value: (x: any) => getFullName(x),
				key: 'id',
				items: this.users,
				f: (keySelected: string) => (x: any) => x.user.id === keySelected
			};
		}

		return fd;
	}

	get serviceFiltersDefinitions() {
		const fd: any = {};

		if (this.services != null && this.services.length > 1) {
			fd.SERVICE = {
				label: this.$t('booking_resources.resource'),
				value: (x: any) => x.label,
				key: 'id',
				items: this.services,
				f: (keySelected: string) => (x: any) => x.id === keySelected,
				placeholder: this.$t('booking_resources.all_resources')
			};
		}
		return fd;
	}

	get isUserFilterSelected() {
		return this.users == null || this.users.length === 1 || this.filterModel == null || this.filterModel.selection.USER != null;
	}

	/**
	 * Les services affichés, filtrés.
	 */
	get displayedServices() {
		return this.serviceFilterModel ? this.services.filter(this.serviceFilterModel.predicate) : this.services;
	}


	/** Méthodes */
	async created() {
		await this.dateChanged();
		this.pollData();
	}

	pollData() {
		const that = this;
		if (this.automaticRefreshPeriodInMS > 0) {
			this.polling = setInterval(async () => {
				await that.reloadBookings();
			}, this.automaticRefreshPeriodInMS);
		}
	}

	beforeDestroy() {
		clearInterval(this.polling);
	}

	sliderLabelWithSuffix(n: number) {
		return `${n}x`;
	}

	areAllFiltersSelected() {
		if (this.filterModel == null) {
			return false;
		}
		const selection = this.filterModel.selection;
		const selectionKeys = Object.keys(selection);
		return selectionKeys.filter((k) => selection[k] != null).length === selectionKeys.length;
	}

	async reloadBookings() {
		const date = new Date(this.date);

		const day = date.getDate();
		const month = date.getMonth() + 1;
		const year = date.getFullYear();

		const promises: any[] = [];
		promises.push(this.fetchDayBookings ? this.fetchDayBookings(day, month, year) : null);
		promises.push(this.fetchDayBookableTimeRanges ? this.fetchDayBookableTimeRanges(day, month, year) : null);

		[this.dayBookings, this.dayBookableTimeRanges] = await Promise.all(promises);
	}

	/**
	 * Callback lorsque le jour sélectionné change.
	 */
	@Watch('date')
	async dateChanged() {
		this.isDateChangedLoading = true;
		await this.reloadBookings();
		this.zoom = Math.max(this.minZoom, Math.min(this.zoom, this.maxZoom));
		this.isDateChangedLoading = false;

	}

	/**
	 * Evènements
	 */
	handleEventClick(event: CalendarTimedEvent) {
		if (event.isDisabled) {
			return;
		}
		if (!event.isReadable) {
			if (!event.isReserved) {
				this.$refs.bookingCreationDialog.open(event, this.getSelectedUser());
			}
		}
		else {
			this.$refs.bookingDetailsDialog.open(event);
		}
	}

	/**
	 * Lorsque la création d'un booking est un succès.
	 */
	async handleCreateSuccess() {
		await this.reloadBookings();
	}

	/**
	 * Lorsque la suppression d'un booking est un succès.
	 */
	async handleDeleteSuccess() {
		await this.reloadBookings();
	}

	/**
	 * L'utilisateur sélectionné dans le filtre, ou le cas échéant, l'utilisateur courant
	 */
	getSelectedUser() {
		return this.users ? (this.filterModel ? this.users.filter(this.filterModel.predicate)[0] : this.users[0]) : null;
	}
}
